import React from "react";
import { Helmet } from "react-helmet-async";

const HomeAbout = () => {
  return (
    <section
      className="relative py-20 px-6 lg:px-20 overflow-hidden"
      aria-labelledby="home-about-heading"
    >
      {/* ✅ SEO Meta for Food Division */}
      <Helmet>
        <title>Clariss Bangladesh | Organic Foods & Healthy Living</title>
        <meta
          name="description"
          content="Clariss Bangladesh offers premium organic foods, healthy snacks, and natural pantry essentials. Shop sustainable, farm-fresh, and eco-friendly food products for a wholesome lifestyle."
        />
        <meta
          name="keywords"
          content="Clariss organic foods, healthy snacks Bangladesh, natural grocery items, farm-fresh products, eco-friendly food, sustainable living, organic pantry"
        />
        <link rel="canonical" href="https://clariss.com.bd" />
      </Helmet>

      {/* ✅ Soft Decorative Background */}
      <div className="absolute -top-20 -left-20 w-[300px] h-[300px] bg-accent/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-[250px] h-[250px] bg-secondary/10 rounded-full blur-3xl"></div>

      {/* ✅ Intro Section */}
      <div className="text-center max-w-4xl mx-auto">
        <span className="text-accent uppercase tracking-widest font-semibold">
          Fresh • Organic • Sustainable
        </span>
        <h2
          id="home-about-heading"
          className="mt-3 text-4xl lg:text-5xl font-extrabold text-secondary leading-tight"
        >
          Clariss – Bringing Nature’s Goodness to Your Table
        </h2>
        <p className="mt-4 text-lg lg:text-xl text-gray-700 font-medium">
          Experience organic foods, wholesome flavors & eco-conscious living.
        </p>
      </div>

      {/* ✅ Two-Column Layout */}
      <div className="mt-16 grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
        {/* ✅ Left Content (Food-oriented) */}
        <div className="text-gray-700 text-base lg:text-lg leading-relaxed animate-fade-in">
          <p className="mb-5">
            <strong>Clariss Bangladesh</strong> brings you{" "}
            <strong>premium organic foods, healthy snacks & pantry essentials</strong>{" "}
            sourced from trusted farms and crafted with love. Every product is{" "}
            <strong>chemical-free, eco-friendly & sustainably packaged</strong> for a healthier lifestyle.
          </p>
          <p className="mb-5">
            From <strong>pure olive oils</strong> and{" "}
            <strong>farm-fresh spices</strong> to{" "}
            <strong>nutritious dry fruits & honey</strong>, Clariss ensures every bite
            is packed with nature’s authentic taste.
          </p>
          <p className="mb-5">
            We believe in <strong>clean eating</strong> and{" "}
            <strong>responsible sourcing</strong> – no artificial preservatives, no harmful
            chemicals, just <strong>honest food for you & your family</strong>.
          </p>
          <div className="mt-8">
            <a
              href="/shop"
              className="inline-block px-10 py-4 bg-accent text-white text-lg font-bold rounded-xl shadow-md hover:shadow-xl hover:bg-secondary transition duration-300"
            >
              🥗 Explore Organic Food Collection
            </a>
          </div>
        </div>

        {/* ✅ Right Visual (Food Image) */}
        <div className="relative animate-fade-in-up">
          <img
            src="https://i.ibb.co/jvFC7Ggq/481985065-621444030804889-7292770462544156477-n.jpg"
            alt="Organic food ingredients on a rustic table"
            className="rounded-2xl shadow-2xl hover:scale-[1.02] transition-transform duration-500"
          />
          {/* Floating Badge */}
          <div className="absolute -top-10 -right-6 bg-white shadow-lg p-4 rounded-xl">
            <p className="text-accent font-bold text-sm">100% Organic</p>
            <p className="text-xs text-gray-500">Farm Fresh Certified</p>
          </div>
        </div>
      </div>

      {/* ✅ Why Choose Clariss Foods */}
      <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
        {[
          {
            icon: "🥗",
            title: "Pure & Natural",
            desc: "No preservatives, no chemicals – only natural ingredients for a healthy diet.",
          },
          {
            icon: "🌿",
            title: "Sustainably Sourced",
            desc: "We partner with ethical farms & support eco-friendly production.",
          },
          {
            icon: "🍯",
            title: "Farm-to-Table Freshness",
            desc: "From organic honey to fresh spices, every product is full of goodness.",
          },
          {
            icon: "🇧🇩",
            title: "Made for Your Family",
            desc: "Nutritious food crafted for Bangladesh’s taste & lifestyle needs.",
          },
        ].map((item, i) => (
          <div
            key={i}
            className="bg-white text-center shadow-md p-8 rounded-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
          >
            <div className="text-4xl">{item.icon}</div>
            <h3 className="mt-4 text-xl font-bold text-secondary">
              {item.title}
            </h3>
            <p className="mt-2 text-sm text-gray-600">{item.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default HomeAbout;
